import type { APIError } from '../types/api';

const API_URL = import.meta.env.VITE_API_URL;
const DEFAULT_ORG_ID = import.meta.env.VITE_DEFAULT_ORG_ID;
const DEFAULT_USER_ID = import.meta.env.VITE_DEFAULT_USER_ID;

/**
 * APIClientError wraps the backend's structured error response so callers
 * can branch on the `code` field (which is also the i18n key suffix).
 */
export class APIClientError extends Error {
    code: string;
    status: number;
    body: APIError;
    constructor(status: number, body: APIError) {
        super(body.message || body.code);
        this.code = body.code;
        this.status = status;
        this.body = body;
    }
}

interface OrgRequestOptions {
    method?: string;
    body?: unknown;       // JSON-serialized unless body is FormData / Blob
    headers?: Record<string, string>;
    signal?: AbortSignal;
    query?: Record<string, string | number | undefined>;
}

interface FlatRequestOptions extends OrgRequestOptions {
    // For non-tenant-scoped endpoints (health, version).
}

/**
 * Build a tenant-scoped URL: `/api/v1/org/<orgId>/<subpath>`.
 *
 * IGNODE convention (matches agent-builder, file-proxy, our own license
 * endpoint): org_id lives in the URL path, not as a query parameter.
 */
function buildOrgUrl(orgId: string, subpath: string): string {
    if (!subpath.startsWith('/')) subpath = '/' + subpath;
    return `${API_URL}/api/v1/org/${orgId}${subpath}`;
}

/**
 * apiOrgFetch is the typed fetch wrapper for every TENANT-SCOPED call.
 *
 * URL shape:
 *   {API_URL}/api/v1/org/{orgId}/{subpath}[?query]
 *
 * orgId defaults to VITE_DEFAULT_ORG_ID when not provided (standalone-mode
 * convenience). In IGNODE mode the upstream proxy still validates the
 * URL's orgId against the user's JWT before forwarding.
 */
export async function apiOrgFetch<T>(
    subpath: string,
    orgId: string | undefined,
    opts: OrgRequestOptions = {},
): Promise<T> {
    const effectiveOrg = orgId || DEFAULT_ORG_ID;
    if (!effectiveOrg) {
        throw new Error('apiOrgFetch: no org_id available — set VITE_DEFAULT_ORG_ID or pass orgId arg');
    }
    let url = buildOrgUrl(effectiveOrg, subpath);

    if (opts.query) {
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(opts.query)) {
            if (v === undefined || v === null) continue;
            params.set(k, String(v));
        }
        const qs = params.toString();
        if (qs) url += (url.includes('?') ? '&' : '?') + qs;
    }

    return doFetch<T>(url, opts);
}

/**
 * apiFlatFetch is for tenant-AGNOSTIC endpoints (health, version) that
 * live at /api/v1/...
 */
export async function apiFlatFetch<T>(path: string, opts: FlatRequestOptions = {}): Promise<T> {
    if (!path.startsWith('/')) path = '/' + path;
    let url = `${API_URL}${path}`;
    if (opts.query) {
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(opts.query)) {
            if (v === undefined || v === null) continue;
            params.set(k, String(v));
        }
        const qs = params.toString();
        if (qs) url += (url.includes('?') ? '&' : '?') + qs;
    }
    return doFetch<T>(url, opts);
}

// Shared low-level fetch logic.
async function doFetch<T>(url: string, opts: OrgRequestOptions): Promise<T> {
    const { method = 'GET', body, headers = {}, signal } = opts;

    const finalHeaders: Record<string, string> = { ...headers };
    let finalBody: BodyInit | undefined;
    if (body instanceof FormData || body instanceof Blob) {
        finalBody = body;
    } else if (body !== undefined) {
        finalHeaders['Content-Type'] = finalHeaders['Content-Type'] || 'application/json';
        finalBody = JSON.stringify(body);
    }

    // credentials: 'omit' (default) — backend CORS uses `Access-Control-Allow-Origin: *`
    // which is incompatible with credentials: 'include' (browser refuses).
    // Standalone has no cookies; IGNODE mode auths via headers (X-Service-Auth) + path
    // params (:orgId, :userId), not cookies. Drop credentials to let the wildcard CORS work.
    const res = await fetch(url, { method, headers: finalHeaders, body: finalBody, signal });

    if (res.status === 204) return undefined as T;

    const contentType = res.headers.get('content-type') || '';
    const isJSON = contentType.includes('application/json');

    if (!res.ok) {
        let errBody: APIError;
        try {
            errBody = isJSON ? await res.json() : { code: 'http_' + res.status, message: res.statusText };
        } catch {
            errBody = { code: 'http_' + res.status, message: res.statusText };
        }
        throw new APIClientError(res.status, errBody);
    }

    if (!isJSON) {
        return res as unknown as T;
    }
    return (await res.json()) as T;
}

/** Build a stream URL (image bytes, thumbnail) — used directly as <img src=...>. */
export function orgStreamUrl(orgId: string | undefined, subpath: string): string {
    const effectiveOrg = orgId || DEFAULT_ORG_ID;
    return buildOrgUrl(effectiveOrg, subpath);
}

/**
 * Build a user-scoped URL: `/api/v1/users/<userId>/<subpath>`.
 *
 * User-scope is separate from org-scope: a single user belongs to many
 * orgs, and per-user UI prefs (theme, locale) follow the user across
 * every org. Standalone: caller passes VITE_DEFAULT_USER_ID. IGNODE
 * mode: the upstream proxy rewrites :userId from the JWT-resolved user
 * UUID before forwarding to backend.
 */
function buildUserUrl(userId: string, subpath: string): string {
    if (!subpath.startsWith('/')) subpath = '/' + subpath;
    return `${API_URL}/api/v1/users/${userId}${subpath}`;
}

/**
 * apiUserFetch is the typed fetch wrapper for every USER-SCOPED call
 * (preferences and any future "things that follow the user across orgs").
 *
 * URL shape:
 *   {API_URL}/api/v1/users/{userId}/{subpath}[?query]
 *
 * userId defaults to VITE_DEFAULT_USER_ID when not provided (standalone-mode
 * convenience).
 */
export async function apiUserFetch<T>(
    subpath: string,
    userId: string | undefined = undefined,
    opts: OrgRequestOptions = {},
): Promise<T> {
    const effectiveUser = userId || DEFAULT_USER_ID;
    if (!effectiveUser) {
        throw new Error('apiUserFetch: no user_id available — set VITE_DEFAULT_USER_ID or pass userId arg');
    }
    let url = buildUserUrl(effectiveUser, subpath);

    if (opts.query) {
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(opts.query)) {
            if (v === undefined || v === null) continue;
            params.set(k, String(v));
        }
        const qs = params.toString();
        if (qs) url += (url.includes('?') ? '&' : '?') + qs;
    }

    return doFetch<T>(url, opts);
}

/**
 * IR-8 — the IGNODE-shared per-user preference store. In IGNODE mode the
 * label-factory proxy forwards /api/v1/users/me/preferences (same-origin
 * cookie surface) to users-api with the lf_session cookie's Bearer. Uses the
 * SAME same-origin transport as every other SPA call (apiFlatFetch → doFetch,
 * default credentials). Standalone (VITE_IGNODE_MODE !== 'true') never calls
 * these — the annotator's own /users/:id/preferences endpoints serve prefs.
 */
export const sharedPrefsEnabled = import.meta.env.VITE_IGNODE_MODE === 'true';

// NOTE: the shared store's language key is `locale` (matches users-api +
// Flow Factory convention + the annotator's own internal naming), NOT `language`.
export interface SharedPrefs {
    theme?: string;
    locale?: string;
}

export function getSharedPrefs(): Promise<SharedPrefs> {
    return apiFlatFetch<SharedPrefs>('/api/v1/users/me/preferences');
}

// POST shallow-merges — write ONLY the key you own so you never clobber the other.
export function saveSharedPrefs(partial: SharedPrefs): Promise<SharedPrefs> {
    return apiFlatFetch<SharedPrefs>('/api/v1/users/me/preferences', {
        method: 'POST',
        body: partial,
    });
}
